import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import _ from "lodash";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

import Navbar from "../Dashboard/Navbar";
import { setCompanyData, setAppIsLoading, setAppError } from "../../store/modules/appState/actions";
import Swal from "sweetalert2";
import AddCompanyModal from "./AddCompanyModal";
import AddProjectModal from './AddProjectModal';
import ServerCall from "../../serverCall";
import CompanyDetails from "./CompanyDetails";
import ProjectDetails from './ProjectDetails';
import { fmtDate, StatusChip } from './Helpers';
import FinancialHandler from '../../handlers/FinancialHandler';

// ---------- Main Page ----------
function CompanyProjectPage({
  user,
  appState,
  dispatchSetAppIsLoading,
  dispatchSetAppError,
  dispatchSetCompanyData,
}) {
  const [company, setCompany] = useState(appState.companyData);   //selected one in UI
  const [companyList, setCompanyList] = useState([]);
  const [project, setProject] = useState(appState.projectDetail);   //selected one in UI
  const [projectList, setProjectList] = useState([]);
  const [openAddCompany, setOpenAddCompany] = useState(false);
  const [openAddProject, setOpenAddProject] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(true);
  const serverCall = ServerCall();

  const getCompaniesApi = async () => {
    try {
      const response = await serverCall.customAxios.get(
        '/company',
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const companyChange = async (companyId) => {
    setLoading(true);
    const updatedList = await getCompaniesApi();
    setCompanyList(updatedList);
    const filteredList = updatedList.filter((item) => item.id === companyId);
    const data = filteredList.length === 0 ? updatedList[0] : filteredList[0];  //needed when saved company is deleted
    setCompany(data);
    if(filteredList.length === 0) dispatchSetCompanyData(data);
    await projectChange(appState.projectDetail?.projectId, data.id);   //put the saved project detail
    setLoading(false);
  }

  const projectChange = async (projectId, companyId) => {
    setProjectLoading(true);
    const updatedList = await FinancialHandler.fetchProjectList({ companyId });
    setProjectList(updatedList);
    const filteredList = updatedList.filter((item) => item.projectId === projectId);
    const data = filteredList.length === 0 ? updatedList[0] : filteredList[0];//when saved project is not for that company
    setProject(data);
    setProjectLoading(false);
  }

  useEffect(() => {
    appState.companyDataValid === "empty" && Swal.fire({
      icon: "info",
      title: "Empty Data",
      text: "The data for that period is empty. Please select the time range again."
    });
  }, [appState.companyDataValid]);

  useEffect(async () => {
    dispatchSetAppIsLoading(true);
    dispatchSetAppError({ state: false, message: null });
    await companyChange(company.id);
    dispatchSetAppIsLoading(false);
  }, []);

  return appState.isLoading ? (
    <>
      <Navbar />
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    </>
  ) : appState.isError ? (
    <div>
      <Navbar />
      <Typography variant="h4" gutterBottom style={{ display: "flex", justifyContent: "center", margin: "30px" }}>
        Error in fetching the data. Click below to go back.
      </Typography>
      <Link style={{ display: "flex", justifyContent: "center", margin: "30px" }} href="/projects" underline="hover">
        Project Selection
      </Link>
    </div>
  ) : (
    <div className="min-h-screen bg-slate-50">
      {/* Modals */}
      <AddCompanyModal open={openAddCompany} onClose={() => setOpenAddCompany(false)} companyList={companyList} />
      <AddProjectModal
        open={openAddProject}
        onClose={() => setOpenAddProject(false)}
        setLoading={setLoading}
        projectChange={projectChange}   //to re-render UI after creating the project
        company={company}
      />

      <Navbar />
      <Box
        sx={{ height: `calc(100vh - 80px)`, maxHeight: `calc(100vh - 80px)` }}
        className="px-4 sm:px-6 lg:px-8 py-4"
      >
        {/* 12-col grid: left 3, middle 3, right 6 */}
        <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
          {/* Companies (full height) */}
          <Grid item xs={12} lg={2} sx={{ height: { xs: 'auto', lg: '100%' } }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                avatar={<BusinessRoundedIcon color="action" />}
                title="Companies"
                subheader={`${companyList.length} total`}
                action={
                  <Tooltip title="Add a new company">
                    <IconButton color="primary" onClick={() => setOpenAddCompany(true)}>
                      <AddRoundedIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <Divider />
              <CardContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
                {companyList.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Typography color="text.secondary">No companies yet. Click + to add one.</Typography>
                  </Box>
                ) : (
                  <List sx={{ py: 0 }}>
                    {companyList.map((c) => (
                      <ListItem key={c.id} disablePadding>
                        <ListItemButton
                          selected={c.id === company.id}
                          onClick={() => companyChange(c.id)}
                          sx={{ '&.Mui-selected': { backgroundColor: 'action.hover' } }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {c.companyName?.[0]?.toUpperCase() || 'C'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Typography fontWeight={600}>{c.companyName}</Typography>}
                            secondary={<StatusChip value={c.status} />}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {loading? (
            <Grid item xs={12} lg={10} sx={{ height: { xs: 'auto', lg: '100%' } }}>
              <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress />
              </Box>
            </Grid>
          ) : (
            <>
              {/* Projects (full height) */}
              <Grid item xs={12} lg={2} sx={{ height: { xs: 'auto', lg: '100%' } }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    avatar={<WorkRoundedIcon color="action" />}
                    title={`Projects ${company ? `(${company.companyName})` : ''}`}
                    subheader={`${projectList.length} total`}
                    action={
                      <Tooltip title="Add a new project">
                        <span>
                          <IconButton 
                            color="primary" 
                            onClick={() => setOpenAddProject(true)} 
                            disabled={company.status === "PENDING"}
                          >
                            <AddRoundedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
                    {!company ? (
                      <Box sx={{ p: 3 }}>
                        <Typography color="text.secondary">Select a company to see its projects.</Typography>
                      </Box>
                    ) : projectList.length === 0 ? (
                      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button
                          startIcon={<AddRoundedIcon />}
                          onClick={() => setOpenAddProject(true)}
                          variant="contained"
                          disabled={company.status === "PENDING"}
                        >
                          Add Project
                        </Button>
                      </Box>
                    ) : (
                      <List sx={{ py: 0 }}>
                        {projectList.map((p) => (
                          <ListItem key={p.projectId} disablePadding>
                            <ListItemButton
                              selected={p.projectId === project?.projectId}
                              onClick={() => projectChange(p.projectId, company.id)}
                              sx={{ '&.Mui-selected': { backgroundColor: 'action.hover' } }}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                  {p.projectName?.[0]?.toUpperCase() || 'P'}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Typography fontWeight={600}>{p.projectName}</Typography>}
                                secondary={
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                                    <CalendarMonthRoundedIcon fontSize="small" />
                                    <Typography variant="caption">
                                      {fmtDate(p.startAt)} — {fmtDate(p.endAt)}
                                    </Typography>
                                  </Stack>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Right column (split height) */}
              <Grid item xs={12} lg={8} sx={{ height: { xs: 'auto', lg: '100%' } }}>
                <Box sx={{ height: '100%', display: 'grid', gridTemplateRows: '1fr 2fr', gap: 2 }}>
                  <CompanyDetails user={user} company={company} />
                  <ProjectDetails
                    appState={appState}
                    isActive={project?.projectId === appState.projectDetail?.projectId}
                    company={company}
                    project={project}
                    setProject={setProject}
                    projectChange={projectChange}
                    loading={projectLoading}
                    setLoading={setProjectLoading}
                  />
                </Box>
              </Grid>
            </>
          )}
          
        </Grid>
      </Box>
    </div>
  );
}

const mapStateToProps = (state) => ({
  appState: state.appState,
});

const mapDispatchToProps = (dispatch) => ({

  dispatchSetAppIsLoading(loading) {
    dispatch(setAppIsLoading(loading));
  },

  dispatchSetCompanyData(companyData) {
    dispatch(setCompanyData(companyData));
  },

  dispatchSetAppError(payload) {
    dispatch(setAppError(payload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyProjectPage);